import clsx from 'clsx';
import * as React from 'react';
import { Button as RACButton } from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';
import { useSafeContext } from '@gitbook/util-react-hooks';

import { Button } from '../Button';
import { DivButton } from '../Sidebar/DivButton';
import {
    HeadlessTree,
    HeadlessTreeItem,
    HeadlessTreeItemContent,
    type HeadlessTreeItemProps,
    type HeadlessTreeProps,
} from './HeadlessTree';

export type TreeProps<T extends object> = HeadlessTreeProps<T>;

export const treeDropIndicatorClassName =
    '[&_.react-aria-DropIndicator]:relative [&_.react-aria-DropIndicator]:h-0 [&_.react-aria-DropIndicator]:z-10 [&_.react-aria-DropIndicator]:ml-[calc((var(--tree-item-level)-1)*1rem)] [&_.react-aria-DropIndicator[data-drop-target=true]]:ring-1 [&_.react-aria-DropIndicator]:ring-neutral-solid';

export function Tree<T extends object>(props: TreeProps<T>) {
    return (
        <HeadlessTree
            {...props}
            className={clsx(
                'rounded-lg border border-control p-1',
                // Drop indicator
                treeDropIndicatorClassName,
                props.className
            )}
        />
    );
}

export type TreeItemProps<T extends object> = HeadlessTreeItemProps<T> & {
    /**
     * Mark the item as active. This is used to highlight the item in the tree.
     */
    isActive?: boolean;
};

export function TreeItem<T extends object>(props: TreeItemProps<T>) {
    const { isActive, ...rest } = props;
    return (
        <HeadlessTreeItem
            {...rest}
            className={clsx(
                'group/tree-item',

                'flex items-center gap-2 rounded-sm p-1.5',
                // Link
                'data-[href]:cursor-pointer data-[href]:data-[hovered]:bg-(--neutral-3)',
                // Active
                isActive && 'bg-(--neutral-4)',
                // Focus
                'data-[focus-visible]:z-[1] data-[focus-visible]:focus-ring',
                // Indent based on the level of the item
                'pl-[calc((var(--tree-item-level)-1)*1rem+0.375rem)]',
                // Dragging
                'data-[dragging]:opacity-drag-preview',
                // Drop target
                'data-[drop-target]:outline-1 data-[drop-target]:outline-neutral data-[drop-target]:outline-dashed',
                props.className
            )}
        />
    );
}

export type TreeItemActionsProps = React.ComponentPropsWithoutRef<'div'> & {
    size?: 'small' | 'medium';
};

/**
 * Context for `TreeItemActions` to provide a consistent size value to its actions.
 */
const TreeItemActionsContext = React.createContext<Pick<TreeItemActionsProps, 'size'>>({
    size: 'small',
});

function TreeItemActionsContextProvider(props: {
    size: TreeItemActionsProps['size'];
    children: React.ReactNode;
}) {
    const { size = 'small', children } = props;
    const value = React.useMemo(() => ({ size }), [size]);
    return (
        <TreeItemActionsContext.Provider value={value}>{children}</TreeItemActionsContext.Provider>
    );
}

function useTreeItemActionsContext() {
    const ctx = useSafeContext(
        TreeItemActionsContext,
        'Use `TreeItemActionsContext` inside `TreeItemActionsContextProvider`'
    );
    return ctx;
}

export function TreeItemActions(props: TreeItemActionsProps) {
    return (
        <TreeItemActionsContextProvider size={props.size}>
            <div
                {...props}
                className={clsx('flex shrink-0 items-center gap-0.5 self-stretch', props.className)}
            />
        </TreeItemActionsContextProvider>
    );
}

export { HeadlessTreeItemContent as TreeItemContent };

export function ExpandSpacer() {
    return <div className="size-6" />;
}

export function ExpandButton() {
    const { size = 'small' } = useTreeItemActionsContext();
    return (
        <Button
            iconOnly
            kind="blank"
            size={size}
            leadingIcon={
                <Icon.ChevronRight className="transition-transform group-aria-expanded/tree-item:rotate-90" />
            }
            label=""
            slot="chevron"
        />
    );
}

export function CollectionExpandButton() {
    return (
        <div className="relative flex shrink-0">
            <DivButton
                slot="chevron"
                preventFocusOnPress
                className={clsx(
                    'peer relative flex shrink-0 rounded-xs text-muted opacity-0 transition',
                    'group-hover/tree-item:opacity-100 group-data-[focus-visible]/tree-item:opacity-100 hover:bg-neutral-ui-hover hover:text-base focus:opacity-100 aria-expanded:bg-neutral-ui-active aria-expanded:opacity-100 data-[pressed=true]:bg-neutral-ui-active',
                    '-outline-offset-1 outline-neutral data-[focus-visible]:outline-1'
                )}
            >
                {
                    <Icon.ChevronRight className="size-4 transition group-aria-expanded/tree-item:rotate-90" />
                }
            </DivButton>
            <div className="pointer-events-none absolute inset-0 flex transition group-hover/tree-item:opacity-0 group-data-[focus-visible]/tree-item:opacity-0 peer-focus:opacity-0">
                <Icon.Collection
                    className={clsx(
                        '-mt-px size-4 shrink-0 text-muted group-hover/tree-item:text-base group-aria-expanded/tree-item-std:text-base group-aria-[current=page]/tree-item:text-base group-data-[active]/tree-item:text-base group-data-[pressed=true]/tree-item:text-base'
                    )}
                />
            </div>
        </div>
    );
}

export function DragHandle() {
    const { size = 'small' } = useTreeItemActionsContext();
    return (
        <div
            className={clsx(
                'inline-flex cursor-grab items-center self-stretch',
                { small: 'px-1', medium: 'px-1.5' }[size]
            )}
        >
            <RACButton
                slot="drag"
                className={clsx(
                    'inline-flex p-0.5 text-muted',
                    'data-[focus-visible]:rounded-sm data-[focus-visible]:focus-ring'
                )}
            >
                <Icon.GrabberVertical
                    className={{ small: 'size-[0.8em]', medium: 'size-4' }[size]}
                />
            </RACButton>
        </div>
    );
}
